<div *ngIf="!isStagedAcquisition" class="acquisition-header" fwMenuCloseTriggers>
  <mat-checkbox [checked]="checked" (change)="checkedChange.emit($event.checked)"
    [attr.data-id]="'acquisitions-select-checkbox'"></mat-checkbox>
  <span class="acquisition-info">
    <span class="acquisition-label" data-id="acquisitions-name-label">{{ acquisition.label }}</span>
    <span class="acquisition-timestamp">{{ timestamp }}</span>
  </span>

  <fw-icon-button color="primary" icon="menu-vertical" size="small" [attr.data-id]="'acquisitions-options-dropdown'" [cdkMenuTriggerFor]="menu"></fw-icon-button>
  <ng-template #menu>
    <fw-menu-container>
      <fw-menu>
        <fw-menu-item *ngIf="permissions.viewMetadata"
          [title]="'common.labels.information' | translate"
          (click)="information()"
        ></fw-menu-item>
        <fw-menu-item *ngIf="permissions.downloadFiles"
          [title]="'acquisitionFiles.download' | translate"
          (click)="downloadFiles()"
        ></fw-menu-item>
        <fw-menu-item *ngIf="permissions.deleteContainer"
          [title]="'acquisitionFiles.delete' | translate"
          (click)="delete()"
        ></fw-menu-item>
        <ng-container *ngIf="permissions.uploadFiles">
          <fw-menu-item [title]="'acquisitionFiles.upload' | translate" (click)="uploadFiles()"></fw-menu-item>
          <fw-menu-item [title]="'acquisitionFiles.create' | translate" (click)="createFile()"></fw-menu-item>
        </ng-container>
        <fw-menu-item *ngIf="permissions.viewNotes"
          [title]="'common.labels.notes' | translate"
          (click)="notes()"
        ></fw-menu-item>
        <ng-container *ngIf="permissions.tasksModify && features.tasksRefactor">
          <fw-menu-separator></fw-menu-separator>
          <fw-menu-item title="Create Reader Task"
            (click)="createReaderTask()"
          ></fw-menu-item>
        </ng-container>
        <ng-container *ngIf="permissions.viewMetadata">
          <fw-menu-separator *ngIf="viewerApps?.length"></fw-menu-separator>
          <fw-menu-item *ngFor="let app of viewerApps"
            [title]="'common.actions.launchIn' | translate | interpolate:app.name"
            (click)="viewAcquisition(app)"
          ></fw-menu-item>
        </ng-container>
      </fw-menu>
    </fw-menu-container>
  </ng-template>
</div>

<!-- Form to create a new acquisition with staged files -->
<div *ngIf="isStagedAcquisition" [formGroup]="stagingForm" class="acquisition-header staged-acquisition">
  <span class="acquisition-info">
    <mat-form-field appearance="outline" class="solid-background">
      <input test-id="acquisition-label-input" matInput formControlName="label" placeholder="New acquisition label" (keydown.enter)="saveStagedAcquisition()">
    </mat-form-field>
  </span>
  <span class="acquisition-buttons">
    <button mat-flat-button color="primary" [disabled]="!stagingForm.valid" (click)="saveStagedAcquisition()">
      {{ 'common.actions.save' | translate }}
    </button>
    <button mat-stroked-button (click)="cancelStagedAcquisition()">{{ 'common.actions.cancel' | translate }}</button>
  </span>
</div>

<div *ngIf="acquisition.files.length === 0" class="file-row">
  <span class="left-icons-wrapper"></span>
  <span>{{ 'acquisitionFiles.noFiles' | translate }}</span>
</div>

<div *ngFor="let file of acquisitionFiles$ | async"
  cdkDrag
  [cdkDragDisabled]="disableFileDrag || !!fileRenames[file.file_id]"
  [cdkDragData]="{file: file, acquisition: acquisition}"
  (cdkDragStarted)="state.fileDragging$.next(file)"
  (cdkDragEnded)="state.fileDragging$.next(null)"
  (click)="selectFile($event, file)"
  class="file-row"
  [ngClass]="{
    disabled: isQuarantined(file),
    'editable-filename': permissions.renameFiles,
    'editable-filetype': permissions.modifyFileMetadata,
    selected: (state.fileSelection$ | async)?.has(file.file_id), invisible: (state.fileDragging$ | async) && (state.fileSelection$ | async)?.has(file.file_id)
  }">
  <div *cdkDragPreview>
    <span *ngIf="(state.fileSelection$ | async)?.size as selectionCount" class="badge-icon">{{ selectionCount }}</span>
    {{ file.name }}
  </div>
  <div *cdkDragPlaceholder>{{ 'acquisitionFiles.dragFilePlaceholder' | translate }}</div>
  <span class="left-icons-wrapper">
    <ng-container *ngIf="getFileOrigin(file) as originInfo">
      <mat-icon *ngIf="originInfo.matIcon"
        [matTooltip]="originInfo.popoverText"
        matTooltipClass="tooltip-stretch"
        class="xs">
        {{ originInfo.matIcon }}
      </mat-icon>
    </ng-container>
    <mat-icon *ngIf="file.copy_of && features.copyByReference" [matTooltip]="'common.labels.referenced' | translate | interpolate:file.copy_of.project_label" class="xs">link</mat-icon>
  </span>
  <span class="file-name" data-id="acquisitions-file-label">
    <fw-ellipsis *ngIf="!fileRenames[file.file_id]" (click)="startFileRename(file)" [text]="file.name" test-id="analysis-input-file-name"></fw-ellipsis>
    <file-version-badge *ngIf="file.version > 1 && !fileRenames[file.file_id]" [file]="file" class="version-badge"></file-version-badge>
    <span *ngIf="fileRenames[file.file_id]" class="file-name-input">
      <mat-form-field appearance="outline">
        <input test-id="file-name-input" matInput autocomplete="false" [formControl]="fileRenames[file.file_id]" (keydown.enter)="saveFileRename(file)">
        <mat-error *ngIf="fileRenames[file.file_id].invalid">{{ getErrorMessage(file.file_id) }}</mat-error>
      </mat-form-field>
      <app-button-group>
        <button test-id="file-name-save" (click)="saveFileRename(file)" mat-icon-button matTooltip="{{ 'common.actions.save' | translate }}" class="xs" [disabled]="fileRenames[file.file_id].invalid">
          <mat-icon class="xs">checkmark</mat-icon>
        </button>
        <button test-id="file-name-cancel" (click)="cancelFileRename(file)" mat-icon-button matTooltip="{{ 'common.actions.close' | translate }}" class="xs">
          <mat-icon class="xs">close</mat-icon>
        </button>
      </app-button-group>
    </span>
  </span>
  <span [matTooltip]="file.created" class="ellipsis file-created" data-id="acquisitions-file-label">
    {{ file.created | date:'yyyy-MM-dd hh:mm'}}
  </span>
  <span [matTooltip]="getFileClassification(file)" class="ellipsis file-classification" data-id="acquisitions-modality-label">
    {{ getFileClassification(file) }}
  </span>
  <div class="file-type-actions">
    <ng-container *ngIf="typeChanges[file.file_id]">
      <mat-form-field appearance="outline">
        <mat-select [formControl]="typeChanges[file.file_id]">
          <mat-option *ngFor="let fileType of fileTypes" [value]="fileType">
            {{ fileType }}
          </mat-option>
        </mat-select>
      </mat-form-field>
      <app-button-group>
        <button test-id="file-type-save" (click)="saveFileTypeSelection(file)" mat-icon-button matTooltip="{{ 'common.actions.save' | translate }}" class="xs">
          <mat-icon class="xs">checkmark</mat-icon>
        </button>
        <button test-id="file-type-cancel" (click)="toggleTypeSelect(file)" mat-icon-button matTooltip="{{ 'common.actions.close' | translate }}" class="xs">
          <mat-icon class="xs">close</mat-icon>
        </button>
      </app-button-group>
    </ng-container>
    <ng-container *ngIf="!typeChanges[file.file_id]">
      <div #fileTypeContainer class="file-type-container">
        <span class="file-type" data-id="acquisitions-type-label" (click)="toggleTypeSelect(file)">{{ file.type }}</span>
        <fw-tooltip *ngIf="showCvatIcon(file)" [title]="getCvatTooltip(file)">
          <mat-icon [ngClass]="[CVATSyncStateClassMap[file?.cvat?.sync_state] || '', 'xs']">
            {{ CVATSyncStateIconMap[file?.cvat?.sync_state] }}
          </mat-icon>
        </fw-tooltip>
      </div>
      <file-actions
        #fileActions
        *ngIf="acquisition?._id && !isQuarantined(file)"
        [file]="file"
        [container]="acquisition"
        [hoverFeatures]="$any(['view', 'info', 'download'])"
        [kabobFeatures]="$any(['info', 'download', 'delete', 'version-history', 'copy-filename', 'copy-modality',
          'view', 'tasks'])"
        [viewParams]="fileViewParameters$ | async"
        (afterChange)="state.fileChanges$.next($event)"
        (afterDelete)="state.fileChanges$.next($event)"
      >
        <fw-icon-button action="annotations" title="Annotations Quick View" size="small" icon="pencil-edit-create"/>
        <fw-tooltip action="cvat" title="Launch in CVAT">
          <fw-icon-button color="primary" icon="video-player-controls" size="small" data-id="acquisitions-file-cvat"></fw-icon-button>
        </fw-tooltip>
        <fw-tooltip action="view" title="Launch in Viewer">
          <fw-icon-button color="primary" icon="image-picture-square" size="small" data-id="acquisitions-file-view"></fw-icon-button>
        </fw-tooltip>
        <fw-tooltip action="info" title="Information">
          <fw-icon-button color="primary" icon="information-circle" size="small" data-id="acquisitions-file-info"></fw-icon-button>
        </fw-tooltip>
        <fw-tooltip action="download" title="Download">
          <fw-icon-button color="primary" icon="download" size="small" data-id="acquisitions-file-download"></fw-icon-button>
        </fw-tooltip>
        <fw-icon-button color="primary" icon="menu-vertical" size="small" data-id="acquisitions-options-dropdown"></fw-icon-button>
      </file-actions>
    </ng-container>
  </div>
</div>
